const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || 'http://localhost:8080/api/v1';

// Raw API response types (Go's sql.Null* types)
interface NullString {
  String: string;
  Valid: boolean;
}

interface NullTime {
  Time: string;
  Valid: boolean;
}

// Raw call from API (before transformation)
interface RawCall {
  id: string;
  call_uuid: string;
  user_id?: NullString | string;
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  status: string;
  initiated_at?: string;
  ringing_at?: NullTime | string;
  answered_at?: NullTime | string;
  ended_at?: NullTime | string;
  duration?: number;
  hangup_cause?: NullString | string;
  recording_url?: NullString | string;
  recording_path?: NullString | string;
  caller_id_name?: NullString | string;
  metadata?: Record<string, any>;
  created_at?: string;
  updated_at?: string;
}

// Transformed call for frontend use
export interface Call {
  id: string;
  call_uuid: string;
  user_id?: string;
  direction: 'inbound' | 'outbound';
  from_number: string;
  to_number: string;
  status: 'initiated' | 'ringing' | 'active' | 'completed' | 'failed' | 'busy' | 'on_hold';
  start_time?: string;
  answer_time?: string;
  end_time?: string;
  duration?: number;
  recording_url?: string;
  hangup_cause?: string;
  caller_id_name?: string;
  metadata?: Record<string, any>;
}

// Helper to extract value from NullString
function extractNullString(value: NullString | string | undefined): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value || undefined;
  return value.Valid ? value.String : undefined;
}

// Helper to extract value from NullTime
function extractNullTime(value: NullTime | string | undefined): string | undefined {
  if (!value) return undefined;
  if (typeof value === 'string') return value || undefined;
  if (!value.Valid) return undefined;
  // Check for Go's zero time
  if (value.Time === '0001-01-01T00:00:00Z') return undefined;
  return value.Time;
}

// Transform raw API call to frontend Call type
function transformCall(raw: RawCall): Call {
  return {
    id: raw.id,
    call_uuid: raw.call_uuid,
    user_id: extractNullString(raw.user_id),
    direction: raw.direction,
    from_number: raw.from_number,
    to_number: raw.to_number,
    status: raw.status as Call['status'],
    start_time: raw.initiated_at,
    answer_time: extractNullTime(raw.answered_at),
    end_time: extractNullTime(raw.ended_at),
    duration: raw.duration,
    recording_url: extractNullString(raw.recording_url),
    hangup_cause: extractNullString(raw.hangup_cause),
    caller_id_name: extractNullString(raw.caller_id_name),
    metadata: raw.metadata,
  };
}

export interface OutboundCallRequest {
  from: string;
  to: string;
  user_id: string;
  caller_id_name?: string;
  record?: boolean;
  metadata?: Record<string, any>;
}

export interface CallControlRequest {
  action: 'hold' | 'unhold' | 'transfer' | 'dtmf';
  target?: string;
  digits?: string;
}

export interface HubSpotUserProvisionResponse {
  id: string;
  hubspot_owner_id: string;
  email: string;
  name: string;
  sip_username: string;
  sip_password: string;
  sip_domain: string;
  active: boolean;
  created_at: string;
  updated_at?: string;
  provisioned?: boolean;
}

class APIClient {
  private baseURL: string;

  constructor() {
    this.baseURL = API_BASE_URL;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const url = `${this.baseURL}${endpoint}`;
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };

    const response = await fetch(url, {
      ...options,
      headers,
    });

    if (!response.ok) {
      const error = await response.json().catch(() => ({ message: response.statusText }));
      throw new Error(error.message || 'API request failed');
    }

    const json = await response.json();

    // Handle wrapped API responses
    if (json && typeof json === 'object' && !Array.isArray(json)) {
      // Format: { success: true, data: ... }
      if ('success' in json && 'data' in json) {
        const data = json.data;
        // If data contains a calls array, extract and transform it (for call history endpoints)
        if (data && typeof data === 'object' && 'calls' in data && Array.isArray(data.calls)) {
          return data.calls.map(transformCall) as T;
        }
        // Single call object - transform if it looks like a call
        if (data && typeof data === 'object' && 'call_uuid' in data) {
          return transformCall(data as RawCall) as T;
        }
        return data as T;
      }
      // Format: { calls: [...], count: N, ... } (call history endpoint - direct response)
      if ('calls' in json && Array.isArray(json.calls)) {
        return json.calls.map(transformCall) as T;
      }
      // Single call object at top level
      if ('call_uuid' in json) {
        return transformCall(json as RawCall) as T;
      }
    }

    return json as T;
  }

  async getStatus() {
    return this.request('/status');
  }

  async initiateOutboundCall(request: OutboundCallRequest): Promise<Call> {
    return this.request<Call>('/calls/outbound', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async getCallDetails(callUUID: string): Promise<Call> {
    return this.request<Call>(`/calls/${callUUID}`);
  }

  async hangupCall(callUUID: string): Promise<void> {
    return this.request(`/calls/${callUUID}`, {
      method: 'DELETE',
    });
  }

  async controlCall(callUUID: string, control: CallControlRequest): Promise<void> {
    return this.request(`/calls/${callUUID}/control`, {
      method: 'POST',
      body: JSON.stringify(control),
    });
  }

  async getCallHistory(userID: string, limit: number = 50): Promise<Call[]> {
    return this.request<Call[]>(`/users/${userID}/calls?limit=${limit}`);
  }

  async registerOutboundCall(request: {
    from: string;
    to: string;
    user_id: string;
    metadata?: Record<string, any>;
  }): Promise<Call> {
    return this.request<Call>('/calls/register', {
      method: 'POST',
      body: JSON.stringify(request),
    });
  }

  async provisionHubSpotUser(ownerId: string): Promise<HubSpotUserProvisionResponse> {
    const endpoint = `/users/hubspot/${ownerId}`;
    const fullUrl = `${this.baseURL}${endpoint}`;
    console.log('[API Client] 📡 Provisioning HubSpot user...');
    console.log('[API Client] Owner ID:', ownerId);
    console.log('[API Client] URL:', fullUrl);
    console.log('[API Client] Base URL:', this.baseURL);

    try {
      const result = await this.request<HubSpotUserProvisionResponse>(endpoint);
      console.log('[API Client] ✅ Provision successful!');
      console.log('[API Client] Response:', result);
      return result;
    } catch (error) {
      console.error('[API Client] ❌ Provision failed:', error);
      throw error;
    }
  }
}

export const apiClient = new APIClient();
